import axios from "axios";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { WarningRounded } from "@material-ui/icons";
import { normalizeAmount } from "../../../app/helpers/normalizeAmount";
import FloatToCurrency from "../../helpers/FloatToCurrency";
import {
  calculateRemainingValue,
  haveToShowRefundInfo
} from "../../helpers/refund";
import { Button } from "../Button/Button";
import Dropdown from "../Dropdown/Dropdown";
import HighlightBanner from "../HighlightedBanner/HighlightBanner";
import { Modal } from "../Modal/Modal";
import ModalConfirm from "../ModalConfirm";
import Failed from "../ModalSubscriptionPolling/components/Failed";
import PollingStatus from "../ModalSubscriptionPolling/components/PollingStatus";
import Success from "../ModalSubscriptionPolling/components/Success";
import { PaymentMethods } from "../PaymentMethods/PaymentMethods";
import * as Sentry from "@sentry/react";
import { shouldUsePlanOffers } from "../../services/subscriptionPlanOffers";
import { createPaymentLink, createPaymentLinkV2 } from "../../helpers/subscription";

const periodicity = {
  30: "Mensal",
  60: "Bimestral",
  90: "Trimestral",
  180: "Semestral",
  365: "Anual",
  450: "Anual-3Meses",
  545: "Anual-6Meses"
};

export default function CreateLinkModal({
  isOpen,
  onClose,
  setPaymentLink,
  originFlow
}) {
  const dispatch = useDispatch();
  const authToken = useSelector(state => state.auth.authToken);
  const [submitting, setSubmitting] = useState(false);
  const [planList, setPlanList] = useState([]);
  const [planSelected, setPlanSelected] = useState(null);
  const [paymentSelected, setPaymentSelected] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [failedReason, setFailedReason] = useState({
    message: "",
    details: ""
  });
  const [pollingScreen, setPollingScreen] = useState("checkout");
  const [boletoUrl, setBoletoUrl] = useState();
  const [isBoletoWarningOpen, setIsBoletoWarningOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsBoletoWarningOpen(false);
    }
  }, [isOpen]);

  const clientSubscription = useSelector(
    state => state.app.client_subscription
  );
  const clientPlanOffers = useSelector(state => state.app.client_plan_offers);
  const isLoadingClientPlanOffers = useSelector(
    state => state.app.is_loading_client_plan_offers
  );
  const customer = useSelector(
    state => state.subscription.customer
  );
  const creditCards = useSelector(state => state.app.cards);

  const newPlanData = useMemo(() => ({
    id: planSelected?.value,
    name: planSelected?.label,
    amount: planSelected?.amount,
    periodicity: planSelected?.periodicity
  }), [planSelected]);

  const currentPlanData = clientSubscription;

  const clientData = useMemo(() => ({
    id: customer?.id,
    email: customer?.email || "",
    name: customer?.name || "",
    provider: clientSubscription?.info?.provider || "pagarme_v5",
  }), [customer, clientSubscription]);

  const basePaymentData = useMemo(() => ({
    method: paymentSelected?.method === "card-list" ? "credit_card" : paymentSelected?.method,
    card_major: paymentSelected?.card?.in_use, 
    card_hash: paymentSelected?.card?.hash ?? paymentSelected?.card?.id,
    card_brand: paymentSelected?.card?.brand,
    card_number_masked: paymentSelected?.card?.last_four_digits ? 
      `**** **** **** ${paymentSelected.card.last_four_digits}` : null
  }), [paymentSelected]);

  const refundInfo = useMemo(() => {
    if (originFlow === "upgrade_plan" && haveToShowRefundInfo(clientSubscription?.info)) {
      const { amount } = calculateRemainingValue(clientSubscription?.info);
      const parsedAmount = `R$ ${FloatToCurrency(amount / 100)}`;
      return { amount, parsedAmount };
    }
    return null;
  }, [originFlow, clientSubscription?.info]);

  const pollingSubscription = async subscription_id => {
    if (clientSubscription?.external_id) {
      setPollingScreen("success");
      return;
    }

    const MAX_POLLING_TIME = 300 * 1000; // 5 minutos
    const startTime = Date.now();

    setPollingStatus("processing");

    const getPollingInterval = elapsedTimeInSeconds => {
      if (elapsedTimeInSeconds <= 60) {
        return 5000; // 5 segundos
      } else if (elapsedTimeInSeconds <= 120) {
        return 10000; // 10 segundos
      } else {
        // restante do tempo (3-5 minutos)
        return 20000; // 20 segundos
      }
    };

    while (Date.now() - startTime < MAX_POLLING_TIME) {
      try {
        const { data: subscription } = await axios.get(
          `${process.env.REACT_APP_API_URL}/subscription/polling/${subscription_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`
            }
          }
        );

        if (subscription.reason?.message) {
          setFailedReason(subscription.reason);
        }

        if (subscription.status === "failed") {
          const payment = {
            ...basePaymentData,
            status: subscription.status
          };

          setSubStatus("failed");
          setPollingScreen("failed");
          return true;
        }

        if (paymentSelected?.method === "boleto") {
          if (subscription.status === "waiting_payment") {
            if (!subscription.tickets || !subscription.tickets.length) {
              toast.info(
                "O link para o boleto gerado será enviado para seu e-mail"
              );
              setSubStatus("boletoPending");
              setPollingScreen("success");
              return true;
            }
            setBoletoUrl(subscription.tickets[0].pdf);
            setSubStatus("bankslipWithUrl");
            setPollingScreen("success");
            return true;
          }
        } else {
          if (subscription.status === "trialing") {
            setSubStatus("trialSuccess");
            setTimeout(() => {
              setPollingScreen("success");
            }, 1000);
            return true;
          }

          if (subscription.status === "active") {
            setSubStatus("activeSuccess");
            setTimeout(() => {
              setPollingScreen("success");
            }, 1000);
            return true;
          }
        }

        const elapsedTimeInSeconds = Math.floor(
          (Date.now() - startTime) / 1000
        );
        const pollingInterval = getPollingInterval(elapsedTimeInSeconds);
        const nextCheckInSeconds = pollingInterval / 1000;

        setPollingStatus(subscription.status);

        let message = `Assinatura com status ${subscription.status}. 
                Será verificado novamente em ${nextCheckInSeconds} segundos. Tempo total: ${elapsedTimeInSeconds} segundos`;

        console.log(message);

        await new Promise(resolve => setTimeout(resolve, pollingInterval));
      } catch (error) {
        console.error("ERROR pollingSubscription", JSON.stringify(error));
        setPollingStatus("failed");
        return false;
      }
    }

    console.log("Numero maximo de tentativas de polling atingido");

    if (paymentSelected?.method === "boleto") {
      setSubStatus("boletoPending");
      setPollingScreen("success");
      return false;
    }
    setPollingStatus("timeout");
    return false;
  };

  function showPeriodicity() {
    if (planSelected) {
      return periodicity[planSelected.periodicity] || "";
    }
    return "";
  }

  useEffect(() => {
    const applyPlansList = plansList => {
      const uniquePlans = plansList.filter(
        (plan, index, self) =>
          index === self.findIndex(p => p.id === plan.id)
      );
      setPlanList(uniquePlans);

      if (
        clientSubscription?.plan_details &&
        originFlow === "reactivation_plan"
      ) {
        const gatewayId = clientSubscription?.plan_details?.gateway_id;
        const matchedPlan = plansList.find(
          plan =>
            plan.id === gatewayId || plan.externalId === gatewayId
        );
        if (matchedPlan) {
          setPlanSelected({
            value: matchedPlan.id,
            label: matchedPlan.name,
            amount: matchedPlan.amount,
            periodicity: matchedPlan.periodicity
          });
        }
      }
    };

    const getPlans = async () => {
      if (isLoadingClientPlanOffers) {
        return;
      }

      const isJussub = !!customer?.external_id;
      if (isJussub && clientPlanOffers.length <= 0) {
        toast.warn("Client jusSub sem ofertas disponíveis.");
        return;
      }

      if (shouldUsePlanOffers(clientPlanOffers)) {
        applyPlansList(clientPlanOffers);
        return;
      }

      try {
        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/subscription/plans_admin"
        );
        if (response.data?.plans) {
          applyPlansList(response.data.plans);
        }
      } catch (err) {
        toast.error("Ocorreu um erro ao carregar os planos.");
      }
    };

    getPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPlanOffers, isLoadingClientPlanOffers]);

  useEffect(() => {
  if (!isOpen) return;

  if (!clientSubscription?.client_id) return;

  dispatch({
    type: "LOAD_CARDS",
    payload: {
        client_id: clientSubscription.client_id,
        useV2: !!clientSubscription.external_id,
      },
  });
}, [isOpen, clientSubscription?.client_id, dispatch]);

  const handleConfirm = async () => {
    const amount = normalizeAmount(planSelected?.amount);
    const minValidAmount = 500;
    const isBankSlip = paymentSelected?.method === "boleto";

    if (planSelected === null) {
      toast.error("Selecione um plano.");
      return;
    }
    if (
      paymentSelected?.method === "card-list" &&
      !paymentSelected?.card
    ) {
      toast.error("Selecione um cartão.");
      return;
    }
    if (amount < minValidAmount && paymentSelected?.method === "boleto") {
      const errorMessage = `Não foi possível realizar a operação. Valor mínimo permitido para boletos: R$${(minValidAmount / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      toast.error(errorMessage);
      return;
    }

    try {
      if (
        (paymentSelected?.method === "card-list" && paymentSelected?.card) ||
        isBankSlip
      ) {

        setSubmitting(true);
        dispatch({
          type: "CREATE_SUBSCRIPTION",
          payload: {
            values: {
              plan_id: {
                id: planSelected?.value,
                name: planSelected?.label,
                amount: String(planSelected?.amount)
              },
              cardholder_name: "",
              card_number: "",
              card_payment_method: null,
              card_cvv: "",
              card_expiration: "",
              card_selected: isBankSlip ? "boleto" : paymentSelected.card,
              client_id: String(clientSubscription?.client_id)
            },
            callback: response => {
              if (response.success && response.data.id) {
                setPollingScreen("polling");
                pollingSubscription(response.data.id);
              } else {
                Sentry.withScope(function(scope) {
                  scope.setTag("flow", originFlow);
                  scope.setTag("payment_method", isBankSlip ? "boleto" : paymentSelected?.method);
                  scope.setExtra("clientData", clientData);
                  if (originFlow === "upgrade_plan") {
                    scope.setExtra("currentPlanData", currentPlanData);
                  }
                  scope.setExtra("newPlanData", newPlanData);
                  scope.setExtra("paymentData", basePaymentData);
                  scope.setTag("provider", clientData.provider);
                  const sanitized = new Error("Create subscription failed on polling");
                  sanitized.name = "Error";
                  Sentry.captureException(sanitized);
                });
                setPollingStatus("failed");
                toast.error(
                  "Não foi possível iniciar o monitoramento da assinatura."
                );
                setSubmitting(false);
              }
            },
            setSubmitting,
            onError: () => {
              Sentry.withScope(function(scope) {
                scope.setTag("flow", originFlow);
                scope.setTag("payment_method", isBankSlip ? "boleto" : paymentSelected?.method);
                scope.setExtra("clientData", clientData);
                if (originFlow === "upgrade_plan") {
                  scope.setExtra("currentPlanData", currentPlanData);
                }
                scope.setExtra("newPlanData", newPlanData);
                scope.setExtra("paymentData", basePaymentData);
                scope.setTag("provider", clientData.provider);
                const sanitized = new Error("Create subscription failed");
                sanitized.name = "Error";
                Sentry.captureException(sanitized);
              });
            }
          }
        });
      }

      if (paymentSelected?.method === "new-card") {
        setSubmitting(true);
        const isJussub = !!clientSubscription?.external_id;
        const linkPayload = {
          planId: String(planSelected?.value),
          customerId: isJussub ? clientSubscription?.external_id : String(clientSubscription?.client_id),
          provider: "pagarme_v5",
          allowedPaymentMethods: ["credit_card"],
          originFlow: originFlow
        };

        try {
          const data = isJussub
            ? await createPaymentLinkV2(linkPayload)
            : await createPaymentLink(linkPayload);
          setPaymentLink(data);
          setSubmitting(false);
        } catch (linkErr) {
          Sentry.withScope(function(scope) {
            scope.setTag("flow", originFlow);
            scope.setTag("payment_method", paymentSelected?.method);
            scope.setExtra("clientData", clientData);
            if (originFlow === "upgrade_plan") {
              scope.setExtra("currentPlanData", currentPlanData);
            }
            scope.setExtra("newPlanData", newPlanData);
            scope.setExtra("paymentData", basePaymentData);
            scope.setTag("provider", clientData.provider);
            const sanitized = new Error("Create subscription failed");
            sanitized.name = "Error";
            Sentry.captureException(sanitized);
          });
          throw linkErr;
        }
      }
    } catch (err) {
      console.log("ERRO: ", err);
      toast.error(
        err?.message || "Ocorreu um erro ao criar o link de pagamento."
      );
      setSubmitting(false);
    }
  };

  const getConfirmButtonLabel = () => {
    if (paymentSelected?.method === "card-list") {
      return "Confirmar e pagar";
    }
    if (paymentSelected?.method === "boleto") {
      return "Gerar Boleto";
    }
    return "Gerar link de pagamento";
  };

  return (
    <>
      {pollingScreen === "checkout" ? (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          title="Condições de Pagamento"
          subtitle="Antes de enviar o link de pagamento para o cliente, confira as informações abaixo."
          style={{ width: "60%" }}
        >
          <Modal.Content>
            <Dropdown
              label="Plano"
              options={planList.map(plan => ({
                value: plan.id,
                label: plan.name,
                amount: plan.amount,
                periodicity: plan.periodicity
              }))}
              value={planSelected}
              onChange={setPlanSelected}
              required
              filterable
            />
            {(originFlow === "url_subscription" ||
              originFlow === "reactivation_plan") && (
              <div className="d-flex flex-column w-100 mt-7">
                <div className="d-flex flex-row justify-content-between align-items-center">
                  <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
                    Periodicidade
                  </p>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                    {showPeriodicity(planSelected?.periodicity) || "-"}
                  </p>
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-5">
                  <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
                    Valor
                  </p>
                  <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                    {planSelected?.amount
                      ? `R$ ${FloatToCurrency(planSelected?.amount || 0)}`
                      : "-"}
                  </p>
                </div>
              </div>
            )}
            {haveToShowRefundInfo(clientSubscription?.info) ? (
              <div
                className="border-top mb-7 mt-5"
                style={{ borderColor: "#E7E8EC" }}
              />
            ) : (
              <div className="mb-7 mt-5" />
            )}
            <PaymentMethods
              defaultCard={
                creditCards.find(card => card.in_use) || creditCards[0]
              }
              cards={creditCards}
              availableMethods={
                creditCards.length === 0
                  ? ["new-card", "boleto"]
                  : ["card-list", "new-card", "boleto"]
              }
              recommendedMethod={
                creditCards.length === 0 ? "new-card" : "card-list"
              }
              onMethodChange={setPaymentSelected}
            />
            {originFlow === "upgrade_plan" &&
              haveToShowRefundInfo(clientSubscription?.info) && (
              <>
                <HighlightBanner type="warning" className="mt-7">
                    Será realizado um estorno no valor de <b>{refundInfo?.parsedAmount || "-"}</b>{" "}
                    e uma cobrança será efetuada com o valor do novo plano
                    escolhido.
                </HighlightBanner>
                <div className="d-flex flex-column w-100 mt-7">
                  <div className="d-flex flex-row justify-content-between align-items-center">
                    <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
                        Periodicidade
                    </p>
                    <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                      {showPeriodicity(planSelected?.periodicity) || "-"}
                    </p>
                  </div>
                  <div className="d-flex flex-row justify-content-between align-items-center mt-5">
                    <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
                        Estorno
                    </p>
                    <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                      {refundInfo?.parsedAmount || "-"}
                    </p>
                  </div>
                  <div className="d-flex flex-row justify-content-between align-items-center mt-5">
                    <p style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
                        Valor
                    </p>
                    <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                      {planSelected?.amount
                        ? `R$ ${FloatToCurrency(planSelected?.amount || 0)}`
                        : "-"}
                    </p>
                  </div>
                </div>
                <div
                  className="border-top mt-5"
                  style={{ borderColor: "#E7E8EC" }}
                />
              </>
            )}
          </Modal.Content>
          <Modal.Footer>
            <Button
              primary
              loading={submitting}
              onClick={() => {
                if (paymentSelected?.method === "boleto") {
                  setIsBoletoWarningOpen(true);
                  return;
                }
                handleConfirm();
              }}
              disabled={planSelected === null}
            >
              {getConfirmButtonLabel()}
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        <Modal
          isOpen={pollingScreen !== "checkout"}
          onClose={() => {
            onClose();
            window.location.reload();
          }}
        >
          {pollingScreen === "polling" && (
            <PollingStatus
              status={pollingStatus}
              isBankSlip={paymentSelected?.method === "boleto"}
            />
          )}
          {pollingScreen === "success" && (
            <Success
              screen={pollingScreen}
              setScreen={setPollingScreen}
              close={() => {
                onClose();
                window.location.reload();
              }}
              status={subStatus}
              bankslipUrl={boletoUrl}
            />
          )}
          {pollingScreen === "failed" && (
            <Failed
              close={onClose}
              title={failedReason.message}
              subTitle={failedReason.details}
            />
          )}
        </Modal>
      )}
      {isOpen && isBoletoWarningOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Tem certeza que quer gerar Boleto para o cliente?"
          message="Boletos gerados aqui são cobrados normalmente."
          onConfirm={() => {
            setIsBoletoWarningOpen(false);
            handleConfirm();
          }}
          onClose={() => setIsBoletoWarningOpen(false)}
        >
          <p
            style={{
              fontWeight: 700,
              color: "#f42a3f",
              backgroundColor: "#FBEAEC",
              borderRadius: 4,
              padding: "10px 12px",
              marginTop: 8
            }}
          >
            Cortesias não devem ser criadas por esse fluxo — use o botão
            &quot;Criar cortesia&quot; na aba Financeiro.
          </p>
        </ModalConfirm>
      )}
    </>
  );
}
